import React, { useState, useEffect } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Heading,
  Flex,
  Button,
  List,
  Text,
  Select,
  Input,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Menu,
  MenuButton,
  Avatar,
  MenuList,
  MenuItem,
  MenuDivider,
  ModalCloseButton,
  FormControl,
  FormHelperText,
  FormLabel,
  useDisclosure,
  IconButton,
  Tooltip,
  ListItem,
  useToast,
} from "@chakra-ui/react";
import { FaUserCircle, FaQuestionCircle } from "react-icons/fa"; // react-icons 사용
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  SUBJECT_OPTIONS,
  QUESTION_TYPE_OPTIONS,
  YEAR_OPTIONS,
  EXAM_OPTIONS,
  ORDERING_OPTIONS,
} from "../constants/answerMetadata";
import AnswerListItem from "../components/AnswerListItem";
import NewAnswerDialog from "../components/NewAnswerDialog";
import NoticeBanner from "../components/NoticeBanner";
import BarExamPromoBanner from "../components/BarExamPromoBanner";
import {
  changePassword,
  clearAuthSession,
  deleteUser,
  fetchAnswers,
} from "../api";

const AnswerList = () => {
  const [answers, setAnswers] = useState([]);
  const [searchParams, setSearchParams] = useSearchParams();
  const [loadingAnswers, setLoadingAnswers] = useState(true);
  const [listError, setListError] = useState("");
  const [listRevision, setListRevision] = useState(0);
  const listQuery = searchParams.toString();
  const changeFilter = (key, value) => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        if (value) next.set(key, value);
        else next.delete(key);
        return next;
      },
      { replace: true },
    );
  };
  const refreshList = () => setListRevision((value) => value + 1);
  const {
    isOpen: isPasswordOpen,
    onOpen: onPasswordOpen,
    onClose: onPasswordClose,
  } = useDisclosure();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const navigate = useNavigate();
  const toast = useToast();

  useEffect(() => {
    const controller = new AbortController();
    let active = true;
    const loadAnswers = async () => {
      setLoadingAnswers(true);
      setListError("");
      try {
        const data = await fetchAnswers(new URLSearchParams(listQuery), {
          signal: controller.signal,
        });
        if (active) setAnswers(data);
      } catch (error) {
        if (active && error.name !== "AbortError")
          setListError(
            "답안 목록을 불러오지 못했습니다. 필터를 초기화하거나 다시 시도해주세요.",
          );
      } finally {
        if (active) setLoadingAnswers(false);
      }
    };

    loadAnswers();
    return () => {
      active = false;
      controller.abort();
    };
  }, [listQuery, listRevision]);

  const handleLogout = () => {
    clearAuthSession();
    navigate("/login"); // 로그인 페이지로 이동
  };

  const handleDeleteUser = async () => {
    const confirmed = window.confirm(
      "정말로 계정을 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.",
    );

    if (!confirmed) {
      // User canceled the deletion
      return;
    }
    try {
      await deleteUser(); // Call the user deletion service
      clearAuthSession();
      navigate("/login");
    } catch (error) {
      console.error("Failed to delete user:", error);
      // Handle error (e.g., show an error message to the user)
    }
  };

  const resetPasswordForm = () => {
    setCurrentPassword("");
    setNewPassword("");
    setNewPasswordConfirm("");
    setPasswordError("");
  };

  const handlePasswordModalClose = () => {
    if (isChangingPassword) return;
    resetPasswordForm();
    onPasswordClose();
  };

  const handleChangePassword = async (event) => {
    event.preventDefault();
    setPasswordError("");

    if (!currentPassword || !newPassword || !newPasswordConfirm) {
      setPasswordError("모든 비밀번호 항목을 입력해주세요.");
      return;
    }

    if (newPassword.length < 8) {
      setPasswordError("새 비밀번호는 8자 이상이어야 합니다.");
      return;
    }

    if (newPassword !== newPasswordConfirm) {
      setPasswordError("새 비밀번호가 일치하지 않습니다.");
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError("새 비밀번호는 현재 비밀번호와 달라야 합니다.");
      return;
    }

    setIsChangingPassword(true);

    try {
      await changePassword(currentPassword, newPassword, newPasswordConfirm);
      resetPasswordForm();
      onPasswordClose();
      toast({
        title: "비밀번호가 변경되었습니다.",
        description: "현재 기기에서는 계속 로그인 상태가 유지됩니다.",
        status: "success",
        duration: 4000,
        isClosable: true,
      });
    } catch (error) {
      setPasswordError(error.message);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <Flex
      direction="column"
      minH="100vh"
      bgGradient="linear(to-r, blue.50, blue.100)"
      p={4}
      alignContent={"center"}
      alignItems={"center"}
    >
      <Flex
        justifyContent="space-between"
        alignItems="center"
        mb={6}
        width={"full"}
      >
        <Heading size="lg">ibt 연습장</Heading>

        {/* 유저 프로필 메뉴 */}
        <Flex>
          <Tooltip
            label={`서비스 제공자 연락처 : ibtpracticehelp@gmail.com`}
            aria-label="Contact tooltip"
          >
            <IconButton
              mb="4"
              icon={<FaQuestionCircle />}
              background="transparent"
            />
          </Tooltip>
          <Menu>
            <MenuButton
              as={Button}
              background="blue.500"
              _hover={{ background: "blue.400" }}
              color="white"
              rightIcon={<FaUserCircle />}
            >
              <Flex alignItems="center">
                <Avatar
                  size="sm"
                  name={localStorage.getItem("nickname")}
                  mr="2"
                />
                <Text>프로필</Text>
              </Flex>
            </MenuButton>
            <MenuList>
              <MenuItem onClick={onPasswordOpen}>비밀번호 변경</MenuItem>
              <MenuDivider />
              <MenuItem onClick={handleDeleteUser} color="red.500">
                회원 탈퇴 하기
              </MenuItem>
              <MenuDivider />
              <MenuItem onClick={handleLogout} color="red.500">
                로그아웃
              </MenuItem>
            </MenuList>
          </Menu>
        </Flex>
      </Flex>
      <BarExamPromoBanner />
      <NewAnswerDialog />
      <NoticeBanner />
      <Box
        p={{ base: "3", md: "6" }}
        rounded="md"
        shadow="md"
        width="full"
        maxW="1000px"
        bg="white"
      >
        <Heading as="h2" mb="6" textAlign="center">
          작성한 답안지 리스트
        </Heading>

        <Flex wrap="wrap" gap="2" mb="3">
          {[
            ["subject", "과목", SUBJECT_OPTIONS],
            ["question_type", "문형", QUESTION_TYPE_OPTIONS],
            ["year", "기출 연도", YEAR_OPTIONS],
            ["exam", "시험", EXAM_OPTIONS],
          ].map(([key, label, options]) => (
            <FormControl key={key} flex="1" minW="130px">
              <FormLabel fontSize="xs" mb="1">
                {label}
              </FormLabel>
              <Select
                size="sm"
                aria-label={`${label} 필터`}
                value={searchParams.get(key) || ""}
                onChange={(event) => changeFilter(key, event.target.value)}
              >
                <option value="">전체</option>
                {options.map(([value, text]) => (
                  <option key={value} value={value}>
                    {text}
                  </option>
                ))}
                <option value="unspecified">미지정</option>
              </Select>
            </FormControl>
          ))}
          <FormControl flex="1" minW="170px">
            <FormLabel fontSize="xs" mb="1">
              정렬
            </FormLabel>
            <Select
              size="sm"
              aria-label="답안 정렬"
              value={searchParams.get("ordering") || "-created_at"}
              onChange={(event) => changeFilter("ordering", event.target.value)}
            >
              {ORDERING_OPTIONS.map(([value, label]) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Select>
          </FormControl>
        </Flex>
        <Flex wrap="wrap" gap="2" mb="4">
          <Button
            size="sm"
            colorScheme="blue"
            variant={
              searchParams.get("favorite") === "true" ? "outline" : "solid"
            }
            onClick={() => changeFilter("favorite", "")}
          >
            전체 답안
          </Button>
          <Button
            size="sm"
            colorScheme="blue"
            variant={
              searchParams.get("favorite") === "true" ? "solid" : "outline"
            }
            onClick={() => changeFilter("favorite", "true")}
          >
            즐겨찾기
          </Button>
          {listQuery && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSearchParams({})}
            >
              필터 초기화
            </Button>
          )}
        </Flex>
        {listError && (
          <Alert status="error" mb="3">
            <AlertIcon />
            {listError}
            <Button size="sm" ml="2" onClick={refreshList}>
              재시도
            </Button>
          </Alert>
        )}
        {loadingAnswers && (
          <Text role="status" fontSize="sm">
            답안을 불러오는 중입니다.
          </Text>
        )}
        {!loadingAnswers && !listError && answers.length === 0 && (
          <Text color="gray.500" py="4">
            조건에 맞는 답안이 없습니다.
          </Text>
        )}

        {/* 답안지 리스트 */}
        <List>
          <ListItem
            justifyContent="space-between"
            alignItems="center"
            p="2"
            borderBottom="1px solid gray"
            width="full"
            display={{ base: "none", md: "flex" }}
          >
            <Flex alignItems="center" flex="2">
              <Text>제목 / 분류</Text>
            </Flex>
            <Text color="gray.500" fontSize="11px" flex="1.3">
              작성일 / 작성시간
            </Text>

            <Flex alignItems="center" fontSize="12px" gap="8">
              <Text>보기 및 공유</Text>
              <Text>파일 작업</Text>
            </Flex>
            <Box width={"80px"} />
          </ListItem>
        </List>

        <List spacing={3}>
          {!loadingAnswers &&
            !listError &&
            answers.map((answer) => (
              <AnswerListItem
                key={answer.answer_id}
                answer={answer}
                answers={answers}
                setAnswers={setAnswers}
                onListChanged={refreshList}
              />
            ))}
        </List>

        <Flex fontSize="sm" color="gray.600" mt="4" justifyContent="center">
          <Button onClick={() => navigate("/policy")} variant={"ghost"}>
            이용약관
          </Button>
          <Box width={"120px"} />
          <Button onClick={() => navigate("/privacy")} variant={"ghost"}>
            개인정보 처리방침
          </Button>
        </Flex>

        <Modal isOpen={isPasswordOpen} onClose={handlePasswordModalClose}>
          <ModalOverlay />
          <ModalContent as="form" onSubmit={handleChangePassword}>
            <ModalHeader>비밀번호 변경</ModalHeader>
            <ModalCloseButton isDisabled={isChangingPassword} />
            <ModalBody>
              {passwordError && (
                <Alert status="error" mb="4" rounded="md">
                  <AlertIcon />
                  <Text fontSize="sm">{passwordError}</Text>
                </Alert>
              )}

              <FormControl mb="4" isRequired>
                <FormLabel>현재 비밀번호</FormLabel>
                <Input
                  type="password"
                  autoComplete="current-password"
                  value={currentPassword}
                  onChange={(event) => setCurrentPassword(event.target.value)}
                  isDisabled={isChangingPassword}
                />
              </FormControl>

              <FormControl mb="4" isRequired>
                <FormLabel>새 비밀번호</FormLabel>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={newPassword}
                  onChange={(event) => setNewPassword(event.target.value)}
                  isDisabled={isChangingPassword}
                />
                <FormHelperText>
                  8자 이상이며 너무 흔하거나 숫자로만 된 비밀번호는 사용할 수
                  없습니다.
                </FormHelperText>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>새 비밀번호 확인</FormLabel>
                <Input
                  type="password"
                  autoComplete="new-password"
                  value={newPasswordConfirm}
                  onChange={(event) =>
                    setNewPasswordConfirm(event.target.value)
                  }
                  isDisabled={isChangingPassword}
                />
              </FormControl>
            </ModalBody>

            <ModalFooter>
              <Button
                type="submit"
                colorScheme="blue"
                mr="3"
                isLoading={isChangingPassword}
                loadingText="변경 중"
              >
                변경하기
              </Button>
              <Button
                onClick={handlePasswordModalClose}
                isDisabled={isChangingPassword}
              >
                취소
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      </Box>
    </Flex>
  );
};

export default AnswerList;
